import { Loading } from '@op/opds.opux';
import { Combobox, ComboboxOption } from '@op/opds.opux-combobox';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce, isEmpty, isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Installments.module.scss';
import { DEBOUNCE_DELAY } from '../../../constants';
import { useSelectedPremiums } from '../../../state/selectors/coverageSelectors';
import { useAppStore } from '../../../state/store';
import type { BackgroundInfoForm } from '../../types/backgroundInfoForm';
import { type PurchasePathState } from '../../types/initialState';
import { validateCalculatorForm } from '../BackgroundInfo/EditableForm';
import { AnalyticsPhases, trackAction } from 'api/analytics-api';
import { post } from 'api/base';
import type { PremiumPerInstallment } from 'dto/CoveragePremium';
import {
  type CoverageProduct,
  type MarketableProduct,
} from 'dto/marketableProduct';
import { type SelectedSumInsuredPremiums } from 'individual/types/selectedSumInsuredPremiums';
import { toEditableForm } from 'state/util';
import { formatEuros, getInitialState, getValidAgeRange } from 'utils';

interface CalculationInput {
  marketableProduct: MarketableProduct;
  backgroundInfoForm: BackgroundInfoForm;
  selectedSumInsuredPremiums: Partial<SelectedSumInsuredPremiums>;
  authenticated: boolean;
}

const shouldRecalculateInstallmentPremiums = (
  newInput: CalculationInput,
  currentInput?: CalculationInput
) => {
  return (
    !validateCalculatorForm(
      toEditableForm(newInput.backgroundInfoForm),
      newInput.authenticated,
      {
        maxMonthlyIncome: newInput.marketableProduct.maxMonthlyIncome,
        maxDebt: newInput.marketableProduct.maxDebt,
        maxChildren: newInput.marketableProduct.maxChildren,
      },
      getValidAgeRange(newInput.marketableProduct)
    ) &&
    !isEmpty(newInput.selectedSumInsuredPremiums) &&
    newInput.marketableProduct.name &&
    !(
      isEqual(newInput.backgroundInfoForm, currentInput?.backgroundInfoForm) &&
      isEqual(
        newInput.selectedSumInsuredPremiums,
        currentInput?.selectedSumInsuredPremiums
      ) &&
      isEqual(
        newInput.marketableProduct.name,
        currentInput?.marketableProduct.name
      )
    )
  );
};

const calculateInstallmentPremiums = async (
  calculationInput: CalculationInput,
  signal?: AbortSignal
): Promise<{ premiumsPerInstallment: PremiumPerInstallment[] } | undefined> => {
  const coveragesMap: Record<
    string,
    { sumInsured: number; deductibleDays?: number | null }
  > = {};
  Object.keys(calculationInput.selectedSumInsuredPremiums).forEach(
    (coverageProduct) => {
      const premium =
        calculationInput.selectedSumInsuredPremiums[
          coverageProduct as CoverageProduct
        ];
      if (premium) {
        coveragesMap[coverageProduct as CoverageProduct] = {
          sumInsured: premium.sumInsured,
          deductibleDays: premium.deductibleDays,
        };
      }
    }
  );
  return await post(
    `/rating/${calculationInput.marketableProduct.name}/installment-premiums`,
    {
      backgroundInfo: calculationInput.backgroundInfoForm,
      coverages: coveragesMap,
    },
    {
      init: {
        signal,
      },
    }
  );
};

const debouncedCalculate = debounce(
  (
    input: CalculationInput,
    abortSignal?: AbortSignal,
    onSuccess?: (result: PremiumPerInstallment[]) => void
  ) => {
    void calculateInstallmentPremiums(input, abortSignal).then((response) => {
      if (response?.premiumsPerInstallment && onSuccess) {
        onSuccess(response.premiumsPerInstallment);
      }
      return response;
    });
  },
  DEBOUNCE_DELAY
);

const Installments = () => {
  const { t } = useTranslation();

  const { marketableProduct, authenticatedCustomer } =
    getInitialState() as PurchasePathState;
  const numberOfInstallments = useAppStore(
    (state) => state.numberOfInstallments
  );
  const setNumberOfInstallments = useAppStore(
    (state) => state.setNumberOfInstallments
  );
  const backgroundInfoForm = useAppStore.use.backgroundInfoForm();
  const selectedPremiums = useSelectedPremiums();
  const selectedCoverages = useAppStore.use.selectedCoverages();

  const [loading, setLoading] = useState(true);
  const [premiumsPerInstallment, setPremiumsPerInstallment] =
    useState<PremiumPerInstallment[]>();
  const calculationValues = useRef<CalculationInput>();
  const abortController = useRef<AbortController>();

  // Form an object of the selectedPremiums where CoverageProduct is the key and Premium is the value.
  // Only include the coverage in the return object if it's selected.
  const selections = Object.keys(selectedPremiums).reduce<
    Partial<SelectedSumInsuredPremiums>
  >((acc, key) => {
    const coverageProduct = key as CoverageProduct;
    const selectedPremium = selectedPremiums[coverageProduct];
    if (selectedCoverages.includes(coverageProduct) && selectedPremium) {
      acc[coverageProduct] = selectedPremium;
    }
    return acc;
  }, {});

  useEffect(() => {
    const currentInput = calculationValues.current;
    const newInput: CalculationInput = {
      selectedSumInsuredPremiums: selections,
      backgroundInfoForm,
      marketableProduct,
      authenticated: authenticatedCustomer,
    };
    calculationValues.current = newInput;

    if (shouldRecalculateInstallmentPremiums(newInput, currentInput)) {
      abortController.current?.abort();
      abortController.current = new AbortController();
      setLoading(true);
      setPremiumsPerInstallment(undefined);
      debouncedCalculate(
        newInput,
        abortController.current?.signal,
        (premiums: PremiumPerInstallment[]) => {
          setPremiumsPerInstallment(premiums);
          setLoading(false);
        }
      );
    }
  }, [
    selectedPremiums,
    selectedCoverages,
    backgroundInfoForm,
    marketableProduct,
  ]);

  const onChange = (value: string | string[]) => {
    const amountOfInstallmentsAsNumber = Number(
      (value as string).split(' - ')[0]
    );
    setNumberOfInstallments(amountOfInstallmentsAsNumber);
    trackAction(
      `laskutuserat-${amountOfInstallmentsAsNumber}-clicked`,
      'button',
      AnalyticsPhases.BENEFICIARIES_AND_DOCUMENTS
    );
  };

  // Using a composite key as a value to force re-render of selected option when calculation completes
  // otherwise loading spinner remains in combobox until selection is changed
  const createKey = (installmentAmount: number) =>
    `${installmentAmount} - ${
      premiumsPerInstallment?.[
        marketableProduct.selectableInstallmentAmounts.indexOf(
          installmentAmount
        )
      ].netPremiumPerInstallment.amount
    }`;

  const formInstallmentsOptionAriaLabel = ({ data }: any): string => {
    if (loading || !premiumsPerInstallment) {
      return t('common.loading');
    }
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${data.installmentAmount} ${t('installments.inInstallments')}${formatEuros(premiumsPerInstallment[data.index].netPremiumPerInstallment.amount, 2)}${t(
      'installments.perInstallment'
    )}
       ${premiumsPerInstallment[data.index].period} ${t('installments.everyXMonth')}`;
  };

  return (
    <div>
      <h2 id='installments-label' className={styles.billingTitle}>
        {t('installments.billing')}
      </h2>
      <Combobox
        formatOptionAriaLabel={formInstallmentsOptionAriaLabel}
        aria-labelledby='installments-label'
        className={styles.combobox}
        onChange={onChange}
        value={createKey(numberOfInstallments)}
        // TODO: Clicking this element won't open the combo box, please fix!
        // To open, query for `[data-testid="installments-combobox"] > div > div`
        data-testid='installments-combobox'>
        {marketableProduct.selectableInstallmentAmounts.map(
          (installmentAmount, index) => (
            <ComboboxOption
              optionData={{ installmentAmount, index }}
              key={createKey(installmentAmount)}
              value={createKey(installmentAmount)}
              data-testid={`installment-option-${installmentAmount}`}>
              <div className={styles.option}>
                <span className={styles.optionText}>
                  {installmentAmount} {t('installments.inInstallments')}
                </span>
                {loading || !premiumsPerInstallment ? (
                  <Loading
                    size={48}
                    data-testid={`installment-option-${installmentAmount}-loading`}
                  />
                ) : (
                  <span
                    data-testid={`installment-option-${installmentAmount}-premium`}>
                    {`${formatEuros(premiumsPerInstallment[index].netPremiumPerInstallment.amount, 2)}${t(
                      'installments.perInstallment'
                    )}`}
                  </span>
                )}
              </div>
              {!loading && premiumsPerInstallment && (
                <small className={styles.description}>
                  {`${premiumsPerInstallment[index].period} ${t('installments.everyXMonth')}`}
                </small>
              )}
            </ComboboxOption>
          )
        )}
      </Combobox>
    </div>
  );
};

export default Installments;
